import { z } from 'zod';

// Password complexity regex - must match backend requirements
// Requires: 8+ chars, lowercase, uppercase, digit, special character
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.,;:_\-+=#^(){}[\]<>])[A-Za-z\d@$!%*?&.,;:_\-+=#^(){}[\]<>]{8,}$/;

const passwordValidation = z
  .string()
  .min(1, 'validation.required')
  .min(8, 'validation.passwordMinLength')
  .regex(PASSWORD_REGEX, 'validation.passwordComplexity');

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'validation.required')
    .email('validation.email'),
  password: z
    .string()
    .min(1, 'validation.required')
    .min(8, 'validation.minLength'),
  rememberMe: z.boolean().optional(),
});

export type LoginFormData = z.infer<typeof loginSchema>;

export const signupSchema = z
  .object({
    email: z.string().min(1, 'validation.required').email('validation.email'),
    name: z.string().min(1, 'validation.required').min(2, 'validation.minLength'),
    birthday: z
      .string()
      .min(1, 'validation.required')
      .refine((value) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return false;
        const now = new Date();
        let age = now.getFullYear() - date.getFullYear();
        const m = now.getMonth() - date.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < date.getDate())) age--;
        return age >= 18 && age <= 120;
      }, 'validation.minAge'),
    password: passwordValidation,
    confirmPassword: z.string().min(1, 'validation.required'),
    city: z.string().min(1, 'validation.required'),
    address: z.string().optional(),
    addressLine2: z.string().optional(),
    postalCode: z.string().optional(),
    landmark: z.string().optional(),
    countryIsoCode3: z.string().min(1, 'validation.required'),
    countryName: z.string().min(1, 'validation.required'),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    acceptTerms: z.boolean().refine((val) => val === true, 'validation.required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'validation.passwordMatch',
    path: ['confirmPassword'],
  });

export type SignupFormData = z.infer<typeof signupSchema>;

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'validation.required')
    .email('validation.email'),
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    password: passwordValidation,
    confirmPassword: z
      .string()
      .min(1, 'validation.required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'validation.passwordMatch',
    path: ['confirmPassword'],
  });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, 'validation.required'),
    newPassword: passwordValidation,
    confirmPassword: z
      .string()
      .min(1, 'validation.required'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'validation.passwordMatch',
    path: ['confirmPassword'],
  });

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
